
<template>
  <div class="relative">
    <!-- Custom Select Input -->
    <div 
      @click.stop="toggleDropdown" 
      class="flex items-center justify-between w-full md: px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9] cursor-pointer"
      :class="{ 'opacity-50': disabled }"
    >
      <div class="flex-grow truncate" :class="{ 'text-gray-400': !hasSelection && !multiple }">
        <template v-if="multiple && selectedOptions.length">
          <div class="flex flex-wrap gap-1">
            <div 
              v-for="(option, index) in selectedOptions" 
              :key="index" 
              class="bg-primaryLight text-grayDark px-2 py-1 rounded-md flex items-center text-xs"
            >
              {{ getLabel(option) }}
              <button 
                @click.stop="removeOption(option)" 
                class="ml-1 focus:outline-none"
              >
                <span class="text-xs">×</span>
              </button>
            </div>
          </div>
        </template>
        <template v-else-if="!multiple && hasSelection">
          {{ getLabel(selectedOption) }}
        </template>
        <template v-else>
          {{ placeholder }}
        </template>
      </div>
      <div class="ml-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Dropdown Menu -->
    <div 
      v-show="isOpen && !disabled" 
      class="absolute mt-1 w-full z-10 bg-white shadow-lg max-h-60 rounded-md overflow-auto"
    >
      <div class="sticky top-0 bg-white px-3 py-2 border-b border-gray-100" v-if="searchable">
        <input 
          type="text" 
          v-model="searchQuery" 
          @click.stop 
          placeholder="Search..." 
          class="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          ref="searchInput"
        />
      </div>
      
      <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-gray-500">
        No options available
      </div>
      
      <div 
        v-for="(option, index) in filteredOptions" 
        :key="index" 
        @click.stop="selectOption(option)"
        class="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
        :class="{ 'bg-gray-100': isSelected(option) }"
      >
        <div class="flex items-center">
          <input 
            v-if="multiple" 
            type="checkbox" 
            :checked="isSelected(option)" 
            class="mr-2" 
            @click.stop
          />
          {{ getLabel(option) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSelect',
  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    modelValue: {
      type: [String, Number, Array, Object],
      default: null
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
    }
  },
  computed: {
    hasSelection() {
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.length > 0;
      }
      return this.modelValue !== null && this.modelValue !== undefined && this.modelValue !== '';
    },
    selectedOption() {
      if (!this.hasSelection) return null;
      
      const option = this.options.find(opt => {
        const optValue = this.getValue(opt);
        return optValue === this.modelValue;
      });
      
      return option;
    },
    selectedOptions() {
      if (!this.multiple || !Array.isArray(this.modelValue) || this.modelValue.length === 0) {
        return [];
      }
      
      const selectedOptions = this.options.filter(opt => {
        const optValue = this.getValue(opt);
        return this.modelValue.includes(optValue);
      });
      
      return selectedOptions;
    },
    filteredOptions() {
      if (!this.searchQuery) return this.options;
      
      const query = this.searchQuery.toLowerCase();
      return this.options.filter(option => {
        const label = this.getLabel(option).toLowerCase();
        return label.includes(query);
      });
    }
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      
      this.isOpen = !this.isOpen;
      
      if (this.isOpen && this.searchable) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    closeDropdown() {
      if (this.isOpen) {
        this.isOpen = false;
        this.searchQuery = '';
      }
    },
    getValue(option) {
      if (!option) return null;
      
      if (typeof option === 'object' && option !== null) {
        if (this.valueKey in option) {
          return option[this.valueKey];
        } else {
          return option.id || option.value || null;
        }
      }
      
      return option;
    },
    getLabel(option) {
      if (!option) return 'Not selected';
      
      if (typeof option === 'object' && option !== null) {
        if (this.labelKey in option) {
          return option[this.labelKey] || 'Unnamed';
        } else {
          return option.name || option.label || option.title || 'Unnamed';
        }
      }
      
      return String(option);
    },
    selectOption(option) {
      const value = this.getValue(option);
      
      if (this.multiple) {
        let newValue = [...(Array.isArray(this.modelValue) ? this.modelValue : [])];
        
        if (this.isSelected(option)) {
          newValue = newValue.filter(v => v !== value);
        } else {
          newValue.push(value);
        }
        
        this.$emit('update:modelValue', newValue);
      } else {
        this.$emit('update:modelValue', value);
        this.closeDropdown();
      }
      
      this.$emit('change', this.multiple ? this.selectedOptions : option);
    },
    removeOption(option) {
      if (!this.multiple) return;
      
      const value = this.getValue(option);
      const newValue = Array.isArray(this.modelValue) 
        ? this.modelValue.filter(v => v !== value) 
        : [];
      
      this.$emit('update:modelValue', newValue);
      this.$emit('change', this.selectedOptions);
    },
    isSelected(option) {
      if (!option) return false;
      
      const value = this.getValue(option);
      
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.includes(value);
      }
      
      return this.modelValue === value;
    }
  },
  watch: {
    options: {
      immediate: true,
      handler(newOptions) {
        if (this.hasSelection && newOptions && newOptions.length > 0) {
          if (this.multiple) {
            this.$nextTick(() => {
              if (this.selectedOptions.length < this.modelValue.length) {
                // Handle multiple selection updates
              }
            });
          } else {
            this.$nextTick(() => {
              if (!this.selectedOption) {
                // Handle single selection updates
              }
            });
          }
        }
      }
    }
  },
  mounted() {
    this.clickOutsideHandler = (event) => {
      if (!this.$el.contains(event.target) && this.isOpen) {
        this.closeDropdown();
      }
    };
    
    document.addEventListener('click', this.clickOutsideHandler, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.clickOutsideHandler, true);
  }
}
</script>
